import { FC } from "react"
import Image from "next/image"
import classNames from "classnames"
import { readImage } from "../../app/utils/read-image"
import { BlokImage } from "../../storyblok"
import { safelyRenderHtml } from "@/util/safelyRenderHtml"
import { useGeoDataEffect } from "../../hooks/useGeoData"
import { PREMIUM_HERO_FORM_ATF_LAYOUT, PremiumHeroForm } from "./hero-form"
import { Anchor } from "@/app/components/anchor"
import StarRating from "@/components/StarRating"
import { useReviewsContext } from "@/context/ReviewsContext"

export interface IPetsHeroATF {
  blok: {
    title: string
    description: string
    buttonLink?: string
    buttonLabel?: string
    image?: BlokImage
    bgSmImage?: BlokImage
    geoPostDescription?: string
    geoTitle?: string
    hasGeoCustomization?: boolean
    hasSwapCustomization?: boolean
    hasListingForm?: boolean
    animalType?: string
    bgImagePlacement?: string
  }
}
const PetsHeroATFVariant: FC<IPetsHeroATF> = (props) => {
  const {
    title,
    description,
    buttonLabel,
    buttonLink,
    bgSmImage,
    image,
    hasGeoCustomization = false,
    hasSwapCustomization = false,
    geoPostDescription = "Hire top-rated transporters in {location}.",
    geoTitle = "",
    hasListingForm = false,
  } = props.blok
  const { geoDescription, geoSessionValue } = useGeoDataEffect(
    geoPostDescription,
  )
  const geoLocationTitle = useGeoDataEffect(geoTitle)
  const { blokReviews } = useReviewsContext()
  const useMobileImage = bgSmImage?.filename ? bgSmImage : image
  return (
    <>
      <section
        id="hero-section"
        className={classNames(
          "relative lg:flex bg-secondary-100 mx-auto justify-center md:pt-8 md:h-[645px]",
        )}
      >
        <div className="flex flex-col md:flex-row w-full justify-center items-start mx-auto py-5 md:pb-2">
          <div className="w-full md:w-1/2 lg:w-2/3 lg:max-w-[651px]">
            <div
              className={`md:h-full relative max-w-4xl lg:max-w-5xl xl:max-w-7xl mx-auto px-6 lg:px-8 pt-1 md:pt-6 pb-2 md:py-8`}
            >
              <div className="text-black font-inter max-w-3xl text-left md:mt-10 lg:mt-20 mb-0">
                <h1 className="text-[42px] leading-[62px] md:leading-[70px] sm:text-[42px] md:text-[56px] font-bold lg:text-[56px] mt-1.5 mb-0.5 md:mt-4 md:mb-6">
                  {geoLocationTitle.geoDescription &&
                  geoLocationTitle.isUSVisitor
                    ? geoLocationTitle.geoDescription
                    : title}
                </h1>
                <div
                  id="PremiumHero-description"
                  className="text-[17px] leading-9 md:leading-10 max-w-lg font-inter mb-0"
                >
                  {safelyRenderHtml(
                    `${description} ${
                      hasGeoCustomization ? geoDescription : ""
                    } `,
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full md:w-1/2 lg:w-1/3 hidden md:block">
            {image && (
              <div className="relative">
                <Image
                  src={readImage(image)}
                  alt={image.alt || "hero image"}
                  width={492}
                  height={428}
                  priority
                  sizes="(max-width: 767px) 0px, 492px"
                  className={classNames(
                    "w-[435px] h-[378px] md:w-[492px] md:h-[428px] order-first md:order-last mb-12 md:mb-0 object-contain object-center",
                    bgSmImage?.filename ? "hidden md:block" : "block",
                  )}
                />
                <section className="sm:w-[180px] md:w-[160px] px-2 pt-2 pb-1 rounded-lg border border-gray-200 bg-white absolute top-1 -right-1">
                  <Image
                    className="h-6 w-auto"
                    src="/icons/google.svg"
                    alt="google logo"
                    width={100}
                    height={30}
                  />

                  <div>
                    <div className="w-full flex flex-row justify-start items-center text-sm">
                      <span className="text-md md:text-lg font-semibold font-work_sans text-black pr-2">
                        {blokReviews?.googleRatings}
                      </span>

                      <StarRating value={5} className="w-24" />
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 mt-4 md:hidden">
          <div className="w-full min-h-[300px] mx-auto flex justify-center relative">
            {useMobileImage?.filename && (
              <Image
                className="object-contain"
                src={readImage(useMobileImage)}
                alt={useMobileImage?.alt ?? "pets"}
                width={320}
                height={400}
                priority
                sizes="(max-width: 767px) 320px, 0px"
              />
            )}
            <section className="sm:w-[180px] md:w-[160px] md:min-h-[75px] p-2 rounded-lg border border-gray-200 bg-white absolute -top-5 -right-2.5">
              <Image
                className="h-[20px] w-auto"
                src="/icons/google.svg"
                alt="google logo"
                width={100}
                height={20}
              />

              <div>
                <div className="w-full flex flex-row justify-start items-center text-sm">
                  <span className="text-md md:text-lg font-semibold font-work_sans text-black pr-2">
                    {blokReviews?.googleRatings}
                  </span>

                  <StarRating value={5} className="w-20" />
                </div>
              </div>
            </section>
          </div>
        </div>
        <div
          className={classNames(
            "relative lg:absolute lg:-bottom-24 max-w-7xl mx-auto md:flex justify-center bg-white rounded-1 px-6 md:px-5 lg:px-8 md:py-11 right-0 left-0",
            hasListingForm ? "md:h-[300px]" : "md:h-[240px]",
          )}
        >
          <div data-testid="form-atf">
            <PremiumHeroForm
              buttonLabel={buttonLabel}
              buttonLink={buttonLink}
              hasSwapCustomization={hasSwapCustomization}
              geoSessionValue={geoSessionValue as string}
              hideLabel
              font="font-inter"
              customClassName={PREMIUM_HERO_FORM_ATF_LAYOUT}
            />
          </div>
        </div>
      </section>

      <section className="md:mb-10" />
    </>
  )
}
export default PetsHeroATFVariant
